import { webmFixDuration } from 'webm-fix-duration'
/**
 * VoiceRecordingService - Handles audio recording with MediaRecorder API
 */
export class VoiceRecordingService {
  constructor(options = {}) {
    this.microphoneManager = options.microphoneManager
    this.mimeType = options.mimeType || this.#getPreferredMimeType()
    this.dataInterval = options.dataInterval || 100 // ms

    // Recording state
    this.mediaRecorder = null
    this.isRecording = false
    this.isPaused = false
    this.audioChunks = []
    this.startTime = null
    this.pausedDuration = 0
    this.pauseStartTime = null

    // Event listeners
    this.eventListeners = new Map()
  }

  /**
   * Start recording audio
   * @returns {Promise<boolean>} Success status
   */
  async start() {
    try {
      const stream = await this.microphoneManager.acquire()
      if (!stream || this.isRecording) {
        return false
      }

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: this.mimeType,
      })

      this.audioChunks = []
      this.isRecording = true
      this.isPaused = false
      this.startTime = Date.now()
      this.pausedDuration = 0

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data)
        }
      }

      this.mediaRecorder.onstart = () => {
        this.#dispatchEvent('started')
      }

      this.mediaRecorder.onstop = async () => {
        const blob = await this.getCurrentAudioBlobWithFixedDuration()
        this.#dispatchEvent('stopped', {
          blob,
          duration: this.getDuration(),
        })
      }

      this.mediaRecorder.start(this.dataInterval)
      return true
    } catch (error) {
      this.isRecording = false
      this.#dispatchEvent('error', { error })
      return false
    }
  }

  /**
   * Stop recording audio
   */
  async stop() {
    if (this.mediaRecorder && this.isRecording) {
      if (this.isPaused) {
        this.resume() // Resume before stopping to ensure proper cleanup
      }
      this.mediaRecorder.stop()
      this.mediaRecorder = null
      this.isRecording = false
      this.isPaused = false

      // Release microphone reference
      await this.microphoneManager.release()
    }
  }

  /**
   * Pause recording
   */
  pause() {
    if (this.mediaRecorder && this.isRecording && !this.isPaused) {
      this.mediaRecorder.pause()
      this.isPaused = true
      this.pauseStartTime = Date.now()
      this.#dispatchEvent('paused')
    }
  }

  /**
   * Resume recording
   */
  resume() {
    if (this.mediaRecorder && this.isRecording && this.isPaused) {
      this.mediaRecorder.resume()
      this.isPaused = false
      if (this.pauseStartTime) {
        this.pausedDuration += Date.now() - this.pauseStartTime
        this.pauseStartTime = null
      }
      this.#dispatchEvent('resumed')
    }
  }

  /**
   * Cancel recording and clean up
   */
  async cancel() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.onstop = null
      this.mediaRecorder.stop()
      this.mediaRecorder = null
      this.isRecording = false
      this.isPaused = false
      this.audioChunks = []

      // Release microphone reference
      await this.microphoneManager.release()

      this.#dispatchEvent('canceled')
    }
  }

  /**
   * Get current audio blob
   */
  getCurrentAudioBlob() {
    if (!this.audioChunks || this.audioChunks.length === 0) {
      return null
    }
    return new Blob(this.audioChunks, { type: this.mimeType })
  }

  /**
   * Get current audio blob with fixed duration metadata
   */
  async getCurrentAudioBlobWithFixedDuration() {
    let blob = this.getCurrentAudioBlob()

    if (this.mimeType.includes('webm')) {
      const duration = this.getDuration()
      blob = await webmFixDuration(blob, duration, this.mimeType)
    }

    return blob
  }

  /**
   * Get recording duration in milliseconds
   */
  getDuration() {
    if (!this.startTime) return 0

    const now =
      this.isPaused && this.pauseStartTime ? this.pauseStartTime : Date.now()
    return now - this.startTime - this.pausedDuration
  }

  /**
   * Create a File object from the current recording
   * @param {string} filename - Name for the file (without extension)
   */
  createAudioFile(filename = 'recording') {
    const blob = this.getCurrentAudioBlob()
    if (!blob) return null

    const fullFilename = filename + this.#getFileExtension()
    return new File([blob], fullFilename, { type: this.mimeType })
  }

  /**
   * Create a File object from the current recording with fixed duration metadata
   * @param {string} filename - Name for the file (without extension)
   */
  async createAudioFileWithFixedDuration(filename = 'recording') {
    const blob = await this.getCurrentAudioBlobWithFixedDuration()
    if (!blob) return null

    const fullFilename = filename + this.#getFileExtension()
    return new File([blob], fullFilename, { type: this.mimeType })
  }

  /**
   * Set audio file on a hidden input element for form submission
   * @param {HTMLInputElement} inputElement - File input element
   * @param {string} filename - Name for the file (without extension)
   */
  setFileOnInput(inputElement, filename = 'recording') {
    const audioFile = this.createAudioFile(filename)
    if (!audioFile || !inputElement) return false

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(audioFile)
    inputElement.files = dataTransfer.files
    return true
  }

  /**
   * Set audio file on a hidden input element for form submission but with fixed duration. Note: this is async, while the unfixed one isn't.
   * @param {HTMLInputElement} inputElement - File input element
   * @param {string} filename - Name for the file (without extension)
   */
  async setFileWithFixedDurationOnInput(inputElement, filename = 'recording') {
    const audioFile = await this.createAudioFileWithFixedDuration(filename)
    if (!audioFile || !inputElement) return false

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(audioFile)
    inputElement.files = dataTransfer.files
    return true
  }

  /**
   * Add event listener
   * @param {string} event - Event name (started, paused, resumed, stopped, canceled, error)
   * @param {Function} callback - Callback function
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set())
    }
    this.eventListeners.get(event).add(callback)
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   */
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).delete(callback)
    }
  }

  /**
   * Clean up resources
   */
  async destroy() {
    await this.cancel()
    this.eventListeners.clear()
  }

  // Private methods

  #getPreferredMimeType() {
    if (MediaRecorder.isTypeSupported('audio/mp4')) {
      return 'audio/mp4'
    } else {
      return 'audio/webm'
    }
  }

  #getFileExtension() {
    if (this.mimeType.includes('mp4')) {
      return '.mp4'
    } else {
      return '.webm'
    }
  }

  #dispatchEvent(eventName, data = {}) {
    if (this.eventListeners.has(eventName)) {
      this.eventListeners.get(eventName).forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error(
            `Error in recording service event listener for ${eventName}:`,
            error
          )
        }
      })
    }
  }
}
